import { Check, Copy, ExternalLink, FileDown, Pencil, Send } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { KeyValueList, Money, StatusBadge, Timeline, TimelineEvent } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import API, { API_URL } from '@/lib/api'
import { Role } from '@/types/User'

import {
  counterpartyOf,
  displayStatus,
  Doc,
  docCurrency,
  DocKind,
  docTotal,
  formatShortDate,
  kindMeta,
  relativeDue,
  statusLabelFor,
} from './documentUtils'

const paymentLabel: Record<string, string> = {
  bankTransfer: 'Bankovní převod',
  card: 'Kartou',
  cash: 'Hotově',
  cod: 'Dobírka',
  other: 'Jiný',
}

interface DocumentSheetProps {
  kind: DocKind
  doc: Doc | null
  onOpenChange: (open: boolean) => void
  onChanged?: () => void
}

/** Detail dokladu v pravém panelu nad seznamem (Midday). */
export const DocumentSheet = ({ kind, doc, onOpenChange, onChanged }: DocumentSheetProps) => {
  const canEdit = useRoleCheck([Role.owner, Role.manager, Role.employee])
  const [paying, setPaying] = useState(false)
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10))
  const meta = kindMeta[kind]

  if (!doc) return <Sheet open={false} onOpenChange={onOpenChange} />

  const status = displayStatus(doc)
  const currency = docCurrency(doc)
  const counterparty = counterpartyOf(doc, kind)
  const permalink = `${API_URL}/api/v2/permalink/${meta.segment}/${doc.uuid}`
  const due = relativeDue(doc.deadlineAt)
  const isPaid = !!doc.paidAt

  const markPaid = async () => {
    try {
      const { status: code } = await API.post(`/api/v2/paid-${meta.segment}s/${doc.uuid}`, {
        paidAt: new Date(paidAt).toISOString(),
      })
      if (code === 204) {
        toast.success('Označeno jako zaplacené')
        setPaying(false)
        onChanged?.()
      } else {
        toast.error('Nepodařilo se uložit platbu')
      }
    } catch {
      toast.error('Nepodařilo se uložit platbu')
    }
  }

  const copyLink = () => {
    navigator.clipboard.writeText(permalink)
    toast.success('Odkaz zkopírován')
  }

  const events: TimelineEvent[] = [
    { label: 'Vytvořeno', date: formatShortDate(doc.createdAt) },
    { label: 'Vystaveno', date: formatShortDate(doc.issuedAt) },
    isPaid
      ? { label: 'Zaplaceno', date: formatShortDate(doc.paidAt) }
      : { label: due?.late ? 'Po splatnosti' : 'Čeká na platbu', date: formatShortDate(doc.deadlineAt), done: false },
  ]

  return (
    <Sheet open={!!doc} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="flex items-center gap-3 border-b px-6 py-4 pr-12">
          <SheetTitle className="text-sm font-medium tabular">{doc.symbol}</SheetTitle>
          <StatusBadge status={status} label={statusLabelFor(kind, status)} />
        </div>
        <SheetDescription className="sr-only">Detail dokladu {doc.symbol}</SheetDescription>

        <div className="space-y-6 px-6 py-6">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{counterparty?.name}</p>
            <Money amount={docTotal(doc)} currency={currency} size="xl" animate />
            <p className={due?.late && !isPaid ? 'text-sm text-danger' : 'text-sm text-muted-foreground'}>
              {isPaid ? `Zaplaceno ${formatShortDate(doc.paidAt)}` : due ? `Splatnost ${formatShortDate(doc.deadlineAt)}, ${due.text}` : null}
            </p>
          </div>

          {canEdit && (
            <div className="flex flex-wrap gap-2">
              {!isPaid && !paying && (
                <Button onClick={() => setPaying(true)}>
                  <Check />
                  Označit jako zaplacené
                </Button>
              )}
              {kind !== 'expense' && (
                <Button variant="outline" asChild>
                  <Link to={`/${meta.segment}/${doc.symbol}/send`}>
                    <Send />
                    Odeslat
                  </Link>
                </Button>
              )}
              <Button variant="outline" asChild>
                <Link to={`/${meta.segment}/${doc.symbol}/edit`}>
                  <Pencil />
                  Upravit
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <a href={permalink} target="_blank" rel="noreferrer">
                  <FileDown />
                  PDF
                </a>
              </Button>
            </div>
          )}

          {paying && (
            <div className="flex flex-wrap items-end gap-2 rounded-lg bg-muted px-4 py-3">
              {/* na mobilu má pole data vlastní šířku (iOS), proto řádek pro sebe a tlačítka pod ním */}
              <label className="min-w-0 basis-full space-y-1 sm:basis-auto sm:flex-1">
                <span className="text-xs text-muted-foreground">Datum úhrady</span>
                <Input type="date" className="w-full min-w-0 appearance-none" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
              </label>
              <Button onClick={markPaid} className="max-sm:flex-1">Uložit</Button>
              <Button variant="ghost" onClick={() => setPaying(false)}>
                Zrušit
              </Button>
            </div>
          )}

          <KeyValueList
            items={[
              { label: meta.counterpartyLabel, value: counterparty?.name ?? '-' },
              ...(counterparty?.ico ? [{ label: 'IČO', value: counterparty.ico }] : []),
              { label: 'Vystaveno', value: formatShortDate(doc.issuedAt) },
              ...(doc.taxableAt ? [{ label: 'DUZP', value: formatShortDate(doc.taxableAt) }] : []),
              { label: 'Splatnost', value: formatShortDate(doc.deadlineAt) },
              { label: 'Způsob platby', value: paymentLabel[doc.paymentMethod ?? ''] ?? '-' },
              ...(doc.vs ? [{ label: 'Variabilní symbol', value: doc.vs }] : []),
              { label: 'Celkem bez DPH', value: <Money amount={docTotal(doc, 'totalPriceTaxFree')} currency={currency} size="sm" /> },
            ]}
          />

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Odkaz na doklad</p>
            <div className="flex">
              <Input readOnly value={permalink} className="rounded-r-none text-xs text-muted-foreground" />
              <Button variant="outline" size="icon" onClick={copyLink} aria-label="Kopírovat odkaz" className="rounded-l-none border-l-0">
                <Copy />
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Položky</p>
            <ul className="divide-y border-y">
              {(doc.items ?? []).map((item, i) => (
                <li key={i} className="flex items-baseline justify-between gap-4 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="truncate">{item.description}</p>
                    <p className="text-xs text-muted-foreground tabular">
                      {item.amount} {item.unit} × <Money amount={item.unitPrice?.amount} currency={item.unitPrice?.currency ?? currency} size="sm" className="font-normal" />
                      {item.taxRate ? `, DPH ${item.taxRate} %` : ''}
                    </p>
                  </div>
                  <Money amount={item.totalPrice?.amount} currency={item.totalPrice?.currency ?? currency} size="sm" />
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Historie</p>
            <Timeline events={events} />
          </div>

          <Button variant="link" asChild>
            <Link to={`/${meta.segment}/${doc.symbol}`}>
              Otevřít celý doklad
              <ExternalLink />
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
